import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedAgendaNota } from '../../types/red'
import { agendaApi, redApi, operatoriAvvisoApi } from '../../services/api'

interface OperatoreAvviso { id: string; nome: string; email?: string | null; telefono_whatsapp?: string | null; attivo: boolean }

interface Props {
  dataIniziale?: Date
  notaEsistente?: RedAgendaNota
  onClose: () => void
  onCreated: (nota: RedAgendaNota) => void
}

interface ImmobileOption {
  id: string
  nome_riferimento: string
}

const toDatetimeLocal = (iso: string) => {
  const d = new Date(iso)
  const tzOffset = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
}

const TIPO_ALTRO = '__altro__'

const NuovaNotaAgendaModal: React.FC<Props> = ({ dataIniziale, notaEsistente, onClose, onCreated }) => {
  const isEdit = !!notaEsistente
  const [immobili, setImmobili] = useState<ImmobileOption[]>([])
  const [immobileId, setImmobileId] = useState(notaEsistente?.immobile_id || '')
  const tipoIniziale = notaEsistente?.tipo || 'nota'
  const tipoInizialeECustom = !['nota', 'appuntamento_telefonico'].includes(tipoIniziale)
  const [tipiCustom, setTipiCustom] = useState<string[]>(tipoInizialeECustom ? [tipoIniziale] : [])
  const [tipo, setTipo] = useState<string>(tipoInizialeECustom ? TIPO_ALTRO : tipoIniziale)
  const [tipoAltroTesto, setTipoAltroTesto] = useState(tipoInizialeECustom ? tipoIniziale : '')
  const [titolo, setTitolo] = useState(notaEsistente?.titolo || '')
  const [testo, setTesto] = useState(notaEsistente?.testo || '')
  const [dataOra, setDataOra] = useState(() => {
    if (notaEsistente) return toDatetimeLocal(notaEsistente.data_ora)
    const d = dataIniziale || new Date()
    return d.toISOString().slice(0, 16)
  })
  const [conReminder, setConReminder] = useState(!!notaEsistente?.reminder_at)
  const [reminderAt, setReminderAt] = useState(notaEsistente?.reminder_at ? toDatetimeLocal(notaEsistente.reminder_at) : '')
  const [canaliEmail, setCanaliEmail] = useState(notaEsistente ? (notaEsistente.canali || []).includes('email') : true)
  const [canaliWhatsapp, setCanaliWhatsapp] = useState(notaEsistente ? (notaEsistente.canali || []).includes('whatsapp') : true)
  const [operatori, setOperatori] = useState<OperatoreAvviso[]>([])
  const [destinatari, setDestinatari] = useState<Set<string>>(new Set(notaEsistente?.destinatari || []))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    redApi.immobili.list().then(res => {
      setImmobili((res.data as { id: string; nome_riferimento: string }[]).map(i => ({ id: i.id, nome_riferimento: i.nome_riferimento })))
    })
    agendaApi.tipiCustom().then(res => {
      setTipiCustom(prev => Array.from(new Set([...prev, ...res.data])).sort())
    }).catch(() => {})
    operatoriAvvisoApi.list().then(res => {
      setOperatori(res.data.filter((o: OperatoreAvviso) => o.email || o.telefono_whatsapp))
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleDestinatario = (id: string) => setDestinatari(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const handleSalva = async () => {
    if (!immobileId) {
      toast.error('Seleziona un immobile')
      return
    }
    if (!titolo.trim()) {
      toast.error('Il titolo è obbligatorio')
      return
    }
    const tipoFinale = tipo === TIPO_ALTRO ? tipoAltroTesto.trim() : tipo
    if (tipo === TIPO_ALTRO && !tipoFinale) {
      toast.error('Specifica il tipo personalizzato')
      return
    }
    if (tipoFinale.length > 30) {
      toast.error('Il tipo personalizzato può avere al massimo 30 caratteri')
      return
    }
    if (conReminder && !reminderAt) {
      toast.error('Imposta data e ora del reminder, oppure disattiva il reminder')
      return
    }
    const canali: string[] = []
    if (canaliEmail) canali.push('email')
    if (canaliWhatsapp) canali.push('whatsapp')
    if (conReminder && canali.length === 0) {
      toast.error('Seleziona almeno un canale per il reminder')
      return
    }
    if (conReminder && destinatari.size === 0) {
      toast.error('Seleziona almeno un destinatario (Operatori Avviso) per il reminder')
      return
    }

    setSaving(true)
    try {
      let res
      if (isEdit && notaEsistente) {
        const payload: Record<string, unknown> = {
          titolo: titolo.trim(),
          testo: testo.trim() || undefined,
          data_ora: new Date(dataOra).toISOString(),
        }
        if (conReminder) {
          payload.reminder_at = new Date(reminderAt).toISOString()
          payload.canali = canali
          payload.destinatari = Array.from(destinatari)
        }
        res = await agendaApi.update(notaEsistente.id, payload)
        toast.success('Nota aggiornata')
      } else {
        res = await agendaApi.create({
          immobile_id: immobileId,
          tipo: tipoFinale,
          titolo: titolo.trim(),
          testo: testo.trim() || undefined,
          data_ora: new Date(dataOra).toISOString(),
          reminder_at: conReminder ? new Date(reminderAt).toISOString() : undefined,
          canali: conReminder ? canali : undefined,
          destinatari: conReminder ? Array.from(destinatari) : undefined,
        })
        toast.success('Nota creata')
      }
      onCreated(res.data)
      onClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || (isEdit ? 'Errore aggiornamento nota' : 'Errore creazione nota'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !saving && onClose()}>
      <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">{isEdit ? 'Modifica nota' : 'Nuova nota agenda'}</h3>
        <div className="space-y-3">
          <div>
            <label className="label text-xs">Immobile</label>
            <select className="select" value={immobileId} onChange={e => setImmobileId(e.target.value)} disabled={isEdit}>
              <option value="">-- Seleziona --</option>
              {immobili.map(i => (
                <option key={i.id} value={i.id}>{i.nome_riferimento}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">Tipo</label>
            <select className="select" value={tipo} onChange={e => setTipo(e.target.value)} disabled={isEdit}>
              <option value="nota">Nota</option>
              <option value="appuntamento_telefonico">Appuntamento telefonico</option>
              {tipiCustom.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value={TIPO_ALTRO}>Altro...</option>
            </select>
            {tipo === TIPO_ALTRO && (
              <input
                className="input mt-2"
                placeholder="Inserisci il nuovo tipo"
                value={tipoAltroTesto}
                onChange={e => setTipoAltroTesto(e.target.value)}
                maxLength={30}
                disabled={isEdit}
              />
            )}
          </div>
          <div>
            <label className="label text-xs">Titolo</label>
            <input className="input" value={titolo} onChange={e => setTitolo(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="label text-xs">Nota descrittiva</label>
            <textarea className="input" rows={3} value={testo} onChange={e => setTesto(e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Data e ora</label>
            <input type="datetime-local" className="input" value={dataOra} onChange={e => setDataOra(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={conReminder} onChange={e => setConReminder(e.target.checked)} />
            Imposta reminder email/WhatsApp
          </label>
          {conReminder && (
            <div className="pl-6 space-y-2 border-l-2 border-blue-100">
              <div>
                <label className="label text-xs">Data e ora reminder</label>
                <input type="datetime-local" className="input" value={reminderAt} onChange={e => setReminderAt(e.target.value)} />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={canaliEmail} onChange={e => setCanaliEmail(e.target.checked)} />
                  Email
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={canaliWhatsapp} onChange={e => setCanaliWhatsapp(e.target.checked)} />
                  WhatsApp
                </label>
              </div>
              <div>
                <label className="label text-xs">Destinatari (Operatori Avviso)</label>
                <div className="space-y-1 max-h-40 overflow-auto border border-gray-100 rounded-lg p-2">
                  {operatori.length === 0 ? (
                    <p className="text-xs text-gray-400 px-1 py-2">Nessun operatore con email o WhatsApp configurato</p>
                  ) : (
                    operatori.map(o => (
                      <label key={o.id} className={`flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-gray-50 cursor-pointer ${!o.attivo ? 'opacity-50' : ''}`}>
                        <input type="checkbox" checked={destinatari.has(o.id)} onChange={() => toggleDestinatario(o.id)} />
                        <span>{o.nome}</span>
                        {!o.attivo && <span className="text-xs text-gray-400">(disattivato)</span>}
                        <span className="text-xs text-gray-400 ml-auto">{[o.email, o.telefono_whatsapp].filter(Boolean).join(' · ')}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSalva} disabled={saving} className="btn-primary text-sm">
            {saving ? (isEdit ? 'Salvataggio...' : 'Creazione...') : (isEdit ? 'Salva' : 'Crea')}
          </button>
          <button onClick={onClose} disabled={saving} className="text-sm text-gray-500">Annulla</button>
        </div>
      </div>
    </div>
  )
}

export default NuovaNotaAgendaModal
